import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { Stack, TextField } from '@mui/material';
import { addTraveller, deleteTraveller, editTraveller, getTravellers } from '../../../api/endpoints/traveller';
import type { Traveller } from '../../../api/types/traveller';
import { ConfirmDialog } from '../../../components/ConfirmDialog';
import { PhoneField } from '../../../components/PhoneField';
import {
  FormDialog,
  ListPageScaffold,
  RowActions,
  useDeleteResource,
  type DataTableColumn,
} from '../../../components/common';

const FIELDS: { name: keyof Traveller; label: string }[] = [
  { name: 'firstName', label: 'Ad' },
  { name: 'lastName', label: 'Soyad' },
  { name: 'gender', label: 'Cinsiyet (Male / Female)' },
  { name: 'birthDate', label: 'Doğum Tarihi (GG.AA.YYYY)' },
  { name: 'nationality', label: 'Uyruk (TR)' },
  { name: 'identity', label: 'T.C. Kimlik No' },
  { name: 'passport', label: 'Pasaport No' },
  { name: 'passportExpireDate', label: 'Pasaport Geçerlilik (GG.AA.YYYY)' },
  { name: 'email', label: 'E-posta' },
  { name: 'phone', label: 'Telefon' },
  { name: 'frequentFlyerNumber', label: 'Mil Kart No' },
  { name: 'additionalInformation', label: 'HES Kodu / Not' },
];

/**
 * "Kayıtlı Yolcu Listesi" — `Traveller/{GetTravellers,AddTraveller,EditTraveller,
 * DeleteTraveller}`. Edit diyaloğu create formu olarak da hizmet eder; `editing.id`
 * Add/Edit ayrımını yapar. Form RHF DEĞİL (backend alanları serbest metin).
 */
export default function TravellerListPage() {
  const qc = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [search, setSearch] = useState('');
  const query = useQuery<Traveller[]>({
    queryKey: ['travellers', 'list'],
    queryFn: () => getTravellers().then((r) => r.travellers ?? []),
  });
  const [editing, setEditing] = useState<Traveller | null>(null);
  const [form, setForm] = useState<Traveller>({});

  const term = search.trim().toLowerCase();
  const all: Traveller[] = query.data ?? [];
  const rows = all.filter((t) => {
    if (!term) return true;
    return [t.firstName, t.lastName, t.fullName, t.identity, t.passport, t.email, t.phone]
      .filter(Boolean)
      .some((v) => String(v).toLowerCase().includes(term));
  });

  const isCreate = !editing?.id;
  const saveMutation = useMutation({
    mutationFn: () => {
      const payload = { ...editing, ...form };
      return isCreate ? addTraveller(payload) : editTraveller(payload);
    },
    onSuccess: (res) => {
      enqueueSnackbar(res.successMessage ?? (isCreate ? 'Yolcu eklendi.' : 'Yolcu kaydedildi.'), { variant: 'success' });
      setEditing(null);
      qc.invalidateQueries({ queryKey: ['travellers'] });
    },
    onError: (err) => enqueueSnackbar(err instanceof Error ? err.message : 'Kayıt başarısız.', { variant: 'error' }),
  });

  const del = useDeleteResource<Traveller>({
    mutationFn: (t) => deleteTraveller(t.id ?? ''),
    invalidateKeys: [['travellers']],
    successMessage: 'Yolcu silindi.',
    errorMessage: 'Silme başarısız.',
  });

  const openForm = (row: Traveller) => {
    setEditing(row);
    setForm(row);
  };

  const columns: DataTableColumn<Traveller>[] = [
    {
      id: 'name',
      header: 'Ad Soyad',
      cell: (r) => r.fullName || [r.firstName, r.lastName].filter(Boolean).join(' ') || '—',
    },
    { id: 'id', header: 'Kimlik / Pasaport', cell: (r) => r.identity || r.passport || '—' },
    { id: 'contact', header: 'İletişim', cell: (r) => [r.email, r.phone].filter(Boolean).join(' · ') || '—' },
  ];

  const delName =
    del.target?.fullName || [del.target?.firstName, del.target?.lastName].filter(Boolean).join(' ');

  return (
    <ListPageScaffold
      title="Kayıtlı Yolcu Listesi"
      createLabel="Yeni Yolcu"
      onCreate={() => {
        setForm({ nationality: 'TR' });
        setEditing({});
      }}
      search={{ value: search, onChange: setSearch, placeholder: 'Ad, soyad veya T.C. kimlik ile ara' }}
      query={{ ...query, data: query.isSuccess ? all : undefined }}
      rows={rows}
      columns={columns}
      getRowKey={(r) => r.id ?? `${r.firstName}-${r.lastName}-${r.email ?? ''}`}
      emptyTitle={search ? 'Eşleşen kayıtlı yolcu yok.' : 'Kayıtlı yolcu yok.'}
      rowActions={(r) => (
        <RowActions onEdit={() => openForm(r)} onDelete={r.id ? () => del.request(r) : undefined} />
      )}
    >
      <FormDialog
        open={!!editing}
        title={isCreate ? 'Yeni Yolcu' : 'Yolcu Düzenle'}
        onClose={() => setEditing(null)}
        onSubmit={() => saveMutation.mutate()}
        submitting={saveMutation.isPending}
        error={saveMutation.error}
      >
        <Stack spacing={2}>
          {FIELDS.map((field) =>
            field.name === 'phone' ? (
              <PhoneField
                key={field.name}
                label={field.label}
                value={(form[field.name] as string | undefined) ?? ''}
                onChange={(v) => setForm((p) => ({ ...p, [field.name]: v }))}
              />
            ) : (
              <TextField
                key={field.name}
                size="small"
                label={field.label}
                value={(form[field.name] as string | undefined) ?? ''}
                onChange={(e) => setForm((p) => ({ ...p, [field.name]: e.target.value }))}
              />
            )
          )}
        </Stack>
      </FormDialog>

      <ConfirmDialog
        {...del.confirmDialogProps}
        title="Yolcuyu sil"
        description={`${delName} kaydını silmek istediğinize emin misiniz?`}
        confirmLabel="Sil"
      />
    </ListPageScaffold>
  );
}
